import { Layout } from "providers/Layout";
import { useApplicantIdReportController } from "./controller";
import { FetchBoundary } from "providers/FetchBoundary";
import { useEffect, useMemo, useState } from "react";
import { ApplicantCardSlice } from "./slices/ApplicantCardSlice";
import { LayoutHeader } from "./components/LayoutHeader";
import { Verification, VerificationNames } from "shared/types";
import { VerificationStatus } from "shared/types";
import { cx } from "shared/utils";
import { combineVerifications } from "shared/utils";
import { CancelOrRefundModal } from "shared/app/CancelOrRefundModal";
import { PrintProvider } from "./providers/PrintProvider";
import { PrintApplication } from "./components/PrintApplication";
import { Redirect } from "shared/components/Redirect";
import { useAuthContext } from "providers/Auth";
import { PrintingLoading } from "./components/PrintingLoading";
import { useLocation } from "react-router-dom";
import logoSquare from "shared/branding/logo-square.svg";
import { RefundModal } from "../../shared/app/RefundModal";

export const ApplicationsIdReportPrintView = () => {
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const { applicationMeta, searchParams, setSearchParams, queryKey } =
    useApplicantIdReportController();

  const location = useLocation();

  const applicant = useMemo(() => {
    return applicationMeta.data?.applicants.length
      ? applicationMeta.data.applicants.find(
          (applicant) =>
            String(applicant.id) === String(searchParams.applicantId)
        )
      : undefined;
  }, [searchParams.applicantId, applicationMeta.data]);

  const currentVerification = applicant?.verifications.find(
    (verification) =>
      verification.name ===
      (searchParams.report || VerificationNames.RentalApplication)
  );

  const verifications = combineVerifications<Verification>(
    ((applicant?.verifications || []) as Verification[]).filter(
      (v) => v.name !== VerificationNames.TermsAndConditions
    ),
    [
      [
        VerificationNames.IncomeVerification,
        VerificationNames.VerifiedIncomeVerification
      ]
    ]
  );

  const { type } = useAuthContext();

  useEffect(() => {
    if (location.state?.refresh === true) {
      location.state = {};
      window.location.reload();
    }
  }, []);

  if (!type.isBoomScreen) return <Redirect href="/" />;

  return (
    <Layout
      headerClassName="bg-white border-b border-alpha-100"
      header={
        <LayoutHeader
          applicationMeta={applicationMeta}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      }
    >
      <FetchBoundary
        loading={applicationMeta.isLoading || !searchParams.applicantId}
        error={applicationMeta.error}
      >
        <PrintProvider>
          {applicationMeta.data ? (
            <div
              className={cx(
                !currentVerification ||
                  ![
                    VerificationStatus.Pending,
                    VerificationStatus.Canceled,
                    VerificationStatus.Started
                  ].includes(currentVerification.status)
                  ? "min-h-full"
                  : "h-full",
                "mx-auto flex max-w-[1200px] flex-col gap-4 print:gap-0 print:bg-white"
              )}
            >
              <ApplicantCardSlice
                applicant={applicant}
                applicationStatus={applicationMeta.data.status}
                onCancelClick={() => setShowCancelModal(true)}
                onRefundClick={() => setShowRefundModal(true)}
                verificationIds={verifications.flat().reduce((acc, curr) => {
                  if (
                    [
                      VerificationNames.IncomeVerification,
                      VerificationNames.HousingHistory
                    ].includes(curr.name)
                  )
                    acc.push(curr.id);

                  return acc;
                }, [] as number[])}
              />
              <div className="flex h-full flex-col rounded-lg border border-alpha-100 bg-white py-6 pl-4 pr-6 shadow-xs print:w-full print:border-none lg:flex-row">
                <div className="min-h-full w-full overflow-y-auto md:pl-6 print:overflow-y-visible print:border-none print:p-0 lg:border-l lg:border-alpha-40">
                  <PrintingLoading />
                </div>
              </div>

              <div className="hidden print:block">
                {applicant ? (
                  <PrintApplication
                    verifications={verifications.flat()}
                    applicant={applicant}
                    applicationMeta={applicationMeta}
                  />
                ) : null}
              </div>

              <br />

              <div
                id="printable_documents_root"
                className="hidden print:block bg-white"
              />

              {/* Printable header/footer styles */}
              <div className="fixed top-0 hidden h-1.5 w-full justify-end px-6 print:flex">
                <img
                  alt="Boom Logo"
                  className="relative top-1 h-[30px] w-[30px]"
                  src={logoSquare}
                />
              </div>
              {/* <div className="fixed bottom-0 hidden h-11 w-full bg-[#090949] print:flex"></div> */}
            </div>
          ) : null}
          <CancelOrRefundModal
            applicationId={
              showCancelModal && applicationMeta.data?.id
                ? String(applicationMeta.data.id)
                : ""
            }
            onSuccess={() => applicationMeta.refetch()}
            onClose={() => setShowCancelModal(false)}
          />
          <RefundModal
            applicationId={
              showRefundModal && applicationMeta.data?.id
                ? String(applicationMeta.data.id)
                : ""
            }
            applicantId={
              showRefundModal && applicant?.id ? String(applicant.id) : ""
            }
            onClose={() => setShowRefundModal(false)}
            queryKey={queryKey}
          />
        </PrintProvider>
      </FetchBoundary>
    </Layout>
  );
};
